/**
 * ============================================================================
 * CARD PAYMENT COMPONENT
 * ============================================================================
 */

/**
 * ============================================================================
 * CARD PAYMENT COMPONENT - 100% Ready (Cameroon Focus)
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';
import { useTransaction } from '../../hooks/useTransaction';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import Spinner from '../common/Spinner';

/**
 * Card Payment Component
 * @returns {JSX.Element}
 */
const CardPayment = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { initiatePayment, loading } = usePayment();
  const { getTransaction, loading: transactionLoading } = useTransaction();
  const { showError } = useToast();
  const { formatCurrency, language } = useI18n();

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  });

  const [errors, setErrors] = useState({});
  const [amount, setAmount] = useState(0);
  const [transaction, setTransaction] = useState(null);

  // Load transaction details
  useEffect(() => {
    const loadTransaction = async () => {
      if (!transactionId) return;
      
      try {
        const transactionData = await getTransaction(transactionId);
        const tx = transactionData?.data?.transaction || transactionData?.data || transactionData;
        setTransaction(tx);
        
        // Calculate total amount (amount + fee)
        const transactionAmount = tx?.amount || 0;
        const fee = Math.min(transactionAmount * 0.015, 15000);
        setAmount(transactionAmount + fee);
      } catch (error) {
        console.error('Load transaction error:', error);
        showError(language === 'fr' ? 'Échec du chargement des détails de la transaction' : 'Failed to load transaction details');
      }
    };

    loadTransaction();
  }, [transactionId, getTransaction, showError, language]);

  const handleChange = (field, value) => {
    let formattedValue = value;

    // Format card number
    if (field === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) return;
    }

    // Format expiry date
    if (field === 'expiryDate') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
      if (formattedValue.length > 5) return;
    }

    // Format CVV
    if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 3) return;
    }

    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.cardNumber.replace(/\s/g, '').match(/^\d{13,19}$/)) {
      newErrors.cardNumber = language === 'fr' ? 'Veuillez entrer un numéro de carte valide' : 'Please enter a valid card number';
    }

    if (!formData.expiryDate.match(/^\d{2}\/\d{2}$/)) {
      newErrors.expiryDate = language === 'fr' ? 'Veuillez entrer une date d\'expiration valide (MM/AA)' : 'Please enter a valid expiry date (MM/YY)';
    }

    if (!formData.cvv.match(/^\d{3,4}$/)) {
      newErrors.cvv = language === 'fr' ? 'Veuillez entrer un CVV valide' : 'Please enter a valid CVV';
    }

    if (!formData.cardholderName.trim()) {
      newErrors.cardholderName = language === 'fr' ? 'Le nom du titulaire de la carte est requis' : 'Cardholder name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payment = await initiatePayment({
        transactionId,
        method: 'card',
        amount,
        card: {
          number: formData.cardNumber.replace(/\s/g, ''),
          expiry: formData.expiryDate,
          cvv: formData.cvv,
          name: formData.cardholderName,
        },
      });

      navigate(`/payment/success/${payment.id}`);
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Le paiement a échoué. Veuillez réessayer.' : 'Payment failed. Please try again.'));
    }
  };

  if (transactionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 animate-fadeInUp">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl shadow-lg">
              💳
            </div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {language === 'fr' ? 'Paiement par carte' : 'Card Payment'}
            </h1>
          </div>
        </div>

        {/* Amount Display */}
        <Card variant="glass" className="mb-6 text-center animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">
            {language === 'fr' ? 'Montant à payer' : 'Amount to Pay'}
          </div>
          <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
            {formatCurrency(amount, 'XAF')}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {language === 'fr' ? 'Incluant les frais' : 'Including fees'}
          </div>
        </Card>

        {/* Form */}
        <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Card Number */}
            <Input
              label={language === 'fr' ? 'Numéro de carte' : 'Card Number'}
              type="text"
              value={formData.cardNumber}
              onChange={(e) => handleChange('cardNumber', e.target.value)}
              placeholder="1234 5678 9012 3456"
              error={errors.cardNumber}
              required
              maxLength={19}
            />

            {/* Expiry and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={language === 'fr' ? 'Date d\'expiration' : 'Expiry Date'}
                type="text"
                value={formData.expiryDate}
                onChange={(e) => handleChange('expiryDate', e.target.value)}
                placeholder="MM/YY"
                error={errors.expiryDate}
                required
                maxLength={5}
              />
              <Input
                label="CVV"
                type="text"
                value={formData.cvv}
                onChange={(e) => handleChange('cvv', e.target.value)}
                placeholder="123"
                error={errors.cvv}
                required
                maxLength={4}
              />
            </div>

            {/* Cardholder Name */}
            <Input
              label={language === 'fr' ? 'Nom du titulaire' : 'Cardholder Name'}
              type="text"
              value={formData.cardholderName}
              onChange={(e) => handleChange('cardholderName', e.target.value)}
              placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
              error={errors.cardholderName}
              required
            />

            {/* Security Badge */}
            <Card variant="outlined" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="text-xl">🔒</span>
                <span>{language === 'fr' ? 'Paiement sécurisé via WekaTrust Escrow' : 'Secure payment via WekaTrust Escrow'}</span>
              </div>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="gradient"
              size="lg"
              fullWidth
              loading={loading}
              className="text-lg py-4 shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Payer' : 'Pay'} {formatCurrency(amount, 'XAF')} →
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CardPayment;
